/* import __COLOCATED_TEMPLATE__ from './leads.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import EmberObject, { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import type AppService from 'embercom/services/app-service';
import { HEIGHT_TYPES, WIDTH_TYPES } from 'embercom/models/reporting/custom/chart';
import { VISUALIZATION_TYPES } from 'embercom/models/reporting/custom/visualization-options';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { get, post } from 'embercom/lib/ajax';
import User from 'embercom/models/user';
import B64FilterParamEncoder from 'embercom/vendor/intercom/b64-filter-param-encoder';
import { getOutcomeMemoryFields } from 'embercom/lib/ai-automation/sdr-memory-fields';
import { QualificationRatesConfig } from 'embercom/components/reporting/bespoke/sdr-leads/qualification-rates/series-config';
import type RouterService from '@ember/routing/router-service';
import type PermissionsModal from 'embercom/services/permissions-modal';
import { checkConversationCreationPermission } from 'embercom/lib/permissions/conversation-creation';
import { type Moment } from 'moment-timezone';

type UserInstance = InstanceType<typeof User>;

type RoutingOutcome = {
  identifier: string;
  title: string;
  workflow: Record<string, unknown> | null;
  fin_instruction_text: string;
  predicates: Array<{ attribute: string; type: string; comparison: string; value: string }>;
  required_memory_fields: Array<{ identifier: string }>;
  optional_memory_fields: Array<{ identifier: string }>;
};

type SdrConfig = {
  app_id: number;
  routing_outcomes: RoutingOutcome[];
  general_instructions: Record<string, unknown> | null;
  integrations: Record<string, unknown> | null;
};

type UserSearchResult = {
  users: UserInstance[];
  totalCount: number;
  totalPages: number;
};

const DEFAULT_CHART_DEFINITION = {
  id: null,
  title: null,
  visualization_type: null,
  metric_property: null,
  metric_function: null,
  stacked: false,
  view_by: null,
  view_by_time_interval: null,
  view_by_display_limit: 10,
  view_by_display_other: false,
  width_type: null,
  height_type: null,
};

interface Signature {
  Args: {};
  Blocks: {
    default: [];
  };
  Element: null;
}

export default class Leads extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare permissionsModal: PermissionsModal;
  @service declare permissionsService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;

  @tracked finSdrConfig: SdrConfig | null = null;
  @tracked selectedOutcomeIdentifier: string | null = null;
  @tracked qualifiedLeads: UserInstance[] = [];
  @tracked totalLeadsCount = 0;
  @tracked currentPage = 1;
  @tracked selectedLeadUser: UserInstance | null = null;
  @tracked isExportModalOpen = false;
  @tracked exportAllAttributes = true;
  @tracked hoveredUserId: string | null = null;
  @tracked selectedUserMap: Record<string, boolean> = {};
  @tracked currentDateRange: { startMoment: Moment; endMoment: Moment } | null = null;
  @tracked showTagModal = false;

  @tracked userFilterSegment = EmberObject.create({
    predicates: [{ attribute: 'role', type: 'role', comparison: 'eq', value: 'contact_role' }],
  });
  @tracked hasActiveUserFilters = false;

  constructor(owner: any, args: Signature['Args']) {
    super(owner, args);
    taskFor(this.loadFinSdrConfig).perform();
  }

  @task({ restartable: true })
  *loadFinSdrConfig(): TaskGenerator<void> {
    try {
      let appId = this.appService.app.id;
      let response = (yield get(`/teammate_app/fin_sdr_config?app_id=${appId}`)) as SdrConfig;
      this.finSdrConfig = response;
      this.intercomEventService.trackAnalyticsEvent({
        action: 'viewed',
        object: 'leads_page',
        place: 'fin_sdr_qualification_analyze',
      });
      let firstOutcome = this.sortedRoutingOutcomes[0];
      if (firstOutcome) {
        this.selectedOutcomeIdentifier = firstOutcome.identifier;
        taskFor(this.loadQualifiedLeads).perform();
      }
    } catch (error: unknown) {
      console.error('Failed to load SDR config:', error);
      this.finSdrConfig = null;
    }
  }

  private get currentLeadPredicates(): Array<Record<string, unknown>> {
    let predicates: Array<Record<string, unknown>> = [
      {
        attribute: 'fin_qualification_status',
        type: 'string',
        comparison: 'eq',
        value: this.selectedOutcomeQualificationStatus,
      },
    ];

    if (this.currentDateRange) {
      let { startMoment, endMoment } = this.currentDateRange;
      if (startMoment) {
        predicates.push({
          attribute: 'created_at',
          type: 'date',
          comparison: 'gt',
          value: {
            year: startMoment.year(),
            month: startMoment.month() + 1,
            day: startMoment.date(),
          },
        });
      }
      if (endMoment) {
        predicates.push({
          attribute: 'created_at',
          type: 'date',
          comparison: 'lt',
          value: {
            year: endMoment.year(),
            month: endMoment.month() + 1,
            day: endMoment.date(),
          },
        });
      }
    }

    let userPredicates = (this.userFilterSegment as $TSFixMe).get('predicates') || [];
    for (let predicate of userPredicates) {
      if (predicate.type !== 'role') {
        predicates.push(predicate);
      }
    }

    return predicates;
  }

  @task({ restartable: true })
  *loadQualifiedLeads(): TaskGenerator<void> {
    let outcome = this.selectedOutcome;
    if (!outcome) {
      return;
    }
    try {
      let result = (yield User.search(this.appService.app, this.currentLeadPredicates, {
        page: this.currentPage,
        per_page: 25,
      })) as UserSearchResult;
      if (this.currentPage === 1) {
        this.qualifiedLeads = [...result.users];
      } else {
        this.qualifiedLeads = [...this.qualifiedLeads, ...result.users];
      }
      this.totalLeadsCount = result.totalCount;
    } catch (e: unknown) {
      console.error('Failed to load qualified leads:', e);
      this.qualifiedLeads = [];
      this.totalLeadsCount = 0;
    }
  }

  get sortedRoutingOutcomes(): RoutingOutcome[] {
    let outcomes = this.finSdrConfig?.routing_outcomes || [];

    let priority = (outcome: RoutingOutcome) => {
      let id = outcome.identifier.toLowerCase();
      if (id === 'disqualified') {
        return 2;
      }
      if (id === 'disengaged') {
        return 3;
      }
      return 1;
    };

    let filtered = [...outcomes].filter(
      (o) => o.identifier.toLowerCase() !== 'escalate_to_support',
    );

    if (filtered.length === 0) {
      return [];
    }

    let disengagedTab: RoutingOutcome = {
      identifier: 'disengaged',
      title: this.intl.t('ai-agent.analyze.leads.disengaged-tab'),
      workflow: null,
      fin_instruction_text: '',
      predicates: [],
      required_memory_fields: [],
      optional_memory_fields: [],
    };

    return [...filtered, disengagedTab].sort((a, b) => priority(a) - priority(b));
  }

  get isDrawerOpen(): boolean {
    return this.selectedLeadUser !== null;
  }

  get drawerUser(): UserInstance {
    return this.selectedLeadUser!;
  }

  @action
  selectLead(user: UserInstance) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'lead_row',
      place: 'fin_sdr_qualification_analyze',
      section: 'leads_table',
      user_id: user.id,
      outcome_identifier: this.selectedOutcomeIdentifier,
    });
    if (this.selectedLeadUser === user) {
      this.selectedLeadUser = null;
    } else {
      this.selectedLeadUser = user;
    }
  }

  @action
  closeDrawer() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'closed',
      object: 'lead_details_drawer',
      place: 'fin_sdr_qualification_analyze',
      section: 'leads_drawer',
    });
    this.selectedLeadUser = null;
  }

  @action
  stopRowClickPropagation(event: Event) {
    event.stopPropagation();
  }

  @action
  handleUserFilterChanged() {
    let predicates = (this.userFilterSegment as $TSFixMe).get('predicates') || [];
    this.hasActiveUserFilters = predicates.some((p: Record<string, unknown>) => p.type !== 'role');
    this.currentPage = 1;
    this.qualifiedLeads = [];
    this.totalLeadsCount = 0;
    taskFor(this.loadQualifiedLeads).perform();
  }

  @action
  clearUserFilters() {
    this.userFilterSegment = EmberObject.create({
      predicates: [{ attribute: 'role', type: 'role', comparison: 'eq', value: 'contact_role' }],
    });
    this.hasActiveUserFilters = false;
    this.currentPage = 1;
    this.qualifiedLeads = [];
    this.totalLeadsCount = 0;
    taskFor(this.loadQualifiedLeads).perform();
  }

  @action
  handleDateRangeChanged(reportState: $TSFixMe) {
    this.currentDateRange = reportState?.dateRange;
    this.currentPage = 1;
    this.qualifiedLeads = [];
    this.totalLeadsCount = 0;
    taskFor(this.loadQualifiedLeads).perform();
  }

  @action
  onRowMouseEnter(userId: string) {
    this.hoveredUserId = userId;
  }

  @action
  onRowMouseLeave() {
    this.hoveredUserId = null;
  }

  @action
  toggleUserSelection(userId: string, event: Event) {
    event.stopPropagation();
    this.selectedUserMap = {
      ...this.selectedUserMap,
      [userId]: !this.selectedUserMap[userId],
    };
  }

  get hasSelectedUsers(): boolean {
    return Object.values(this.selectedUserMap).some(Boolean);
  }

  get selectedUserIds(): string[] {
    return Object.entries(this.selectedUserMap)
      .filter(([_, selected]) => selected)
      .map(([id]) => id);
  }

  get formattedLeadsCount(): string {
    return this.intl.formatNumber(this.totalLeadsCount);
  }

  get exportCount(): number {
    let selectedCount = this.selectedUserIds.length;
    return selectedCount > 0 ? selectedCount : this.totalLeadsCount;
  }

  @action
  selectOutcome(identifier: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'outcome_tab',
      place: 'fin_sdr_qualification_analyze',
      section: 'outcome_selector',
      outcome_identifier: identifier,
    });
    this.selectedOutcomeIdentifier = identifier;
    this.selectedLeadUser = null;
    this.selectedUserMap = {};
    this.currentPage = 1;
    this.qualifiedLeads = [];
    this.totalLeadsCount = 0;
    taskFor(this.loadQualifiedLeads).perform();
  }

  @action
  startNewConversation(userId: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'new_conversation_button',
      place: 'fin_sdr_qualification_analyze',
      section: 'leads_table',
      user_id: userId,
    });
    let allowed = checkConversationCreationPermission({
      appService: this.appService,
      permissionsModal: this.permissionsModal,
      intl: this.intl,
    });
    if (!allowed) {
      return;
    }
    this.router.transitionTo('inbox.workspace.inbox.new-conversation', this.appService.app.id, {
      queryParams: { recipient: userId, overwrite: null },
    });
  }

  get selectedOutcome(): RoutingOutcome | undefined {
    return this.sortedRoutingOutcomes.find((o) => o.identifier === this.selectedOutcomeIdentifier);
  }

  get selectedOutcomeQualificationStatus(): string {
    let outcome = this.selectedOutcome;
    if (!outcome) {
      return '';
    }
    // eslint-disable-next-line @intercom/intercom/no-bare-strings -- backend lookup value, not user-facing
    return outcome.identifier === 'disengaged' ? 'Pending' : outcome.title;
  }

  get isLoadingLeads(): boolean {
    return taskFor(this.loadQualifiedLeads).isRunning;
  }

  get dynamicColumns() {
    let outcome = this.selectedOutcome;
    if (!outcome) {
      return [];
    }
    let fields =
      outcome.identifier === 'disengaged'
        ? this.allOutcomeMemoryFields
        : getOutcomeMemoryFields(outcome);
    return fields.filter((col) => !col.identifier.startsWith('conversation.custom_attribute.'));
  }

  private get allOutcomeMemoryFields() {
    let outcomes = this.finSdrConfig?.routing_outcomes || [];
    let seen = new Set<string>();
    let fields: ReturnType<typeof getOutcomeMemoryFields> = [];
    for (let outcome of outcomes) {
      for (let col of getOutcomeMemoryFields(outcome)) {
        if (!seen.has(col.identifier)) {
          seen.add(col.identifier);
          fields.push(col);
        }
      }
    }
    return fields;
  }

  get tableColumns(): Array<{ label: string; valuePath: string }> {
    let columns = [
      {
        label: this.intl.t('ai-agent.analyze.leads.table.contact-name'),
        valuePath: 'contactName',
      },
      {
        label: '',
        valuePath: 'newConversation',
        width: '40px',
      },
    ];
    for (let col of this.dynamicColumns) {
      columns.push({ label: col.label, valuePath: col.key });
    }
    columns.push({
      label: this.intl.t('ai-agent.analyze.leads.table.conversation'),
      valuePath: 'conversation',
    });
    return columns;
  }

  get leadsTableData(): Array<{ user: UserInstance; conversationUrl: string }> {
    let outcome = this.selectedOutcome;
    if (!outcome) {
      return [];
    }
    return this.qualifiedLeads.map((user: UserInstance) => ({
      user,
      conversationUrl: this.buildConversationSearchUrl(user, outcome),
    }));
  }

  get hasMorePages(): boolean {
    return this.qualifiedLeads.length < this.totalLeadsCount;
  }

  @action
  loadNextPage() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'load_more',
      place: 'fin_sdr_qualification_analyze',
      section: 'leads_table',
      page: this.currentPage + 1,
    });
    this.currentPage += 1;
    taskFor(this.loadQualifiedLeads).perform();
  }

  @action
  trackContactNameClick(userId: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'contact_name_link',
      place: 'fin_sdr_qualification_analyze',
      section: 'leads_table',
      user_id: userId,
    });
  }

  @action
  trackConversationLinkClick(userId: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'view_conversation_link',
      place: 'fin_sdr_qualification_analyze',
      section: 'leads_table',
      user_id: userId,
    });
  }

  @action
  async openExportModal() {
    await this.permissionsService.checkPermission('can_export_data');
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'export_button',
      place: 'fin_sdr_qualification_analyze',
      outcome_identifier: this.selectedOutcomeIdentifier,
    });
    this.isExportModalOpen = true;
  }

  @action
  closeExportModal() {
    this.isExportModalOpen = false;
    this.exportAllAttributes = true;
  }

  get taggableCount(): number {
    let selectedCount = this.selectedUserIds.length;
    return selectedCount > 0 ? selectedCount : this.totalLeadsCount;
  }

  @action
  openTagModal() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'add_tag_button',
      place: 'fin_sdr_qualification_analyze',
      outcome_identifier: this.selectedOutcomeIdentifier,
    });
    this.showTagModal = true;
  }

  @action
  closeTagModal() {
    this.showTagModal = false;
  }

  @action
  updateTaggings(_admin: unknown, addedTags: $TSFixMe[]) {
    return this.bulkTagLeads(addedTags);
  }

  @action
  afterTagSave(addedTags: $TSFixMe[]) {
    this.showTagModal = false;
    let count = this.taggableCount;
    let hasSelection = this.selectedUserIds.length > 0;
    let translationKey =
      count < 200 && hasSelection
        ? 'components.base.user-or-companies-list.tagged-lead'
        : 'components.base.user-or-companies-list.will-tag-lead';
    this.notificationsService.notifyConfirmation(this.intl.t(translationKey, { count }));
    this.intercomEventService.trackAnalyticsEvent({
      action: 'tagged',
      object: 'leads',
      place: 'fin_sdr_qualification_analyze',
      outcome_identifier: this.selectedOutcomeIdentifier,
      tags: addedTags.map((tag: $TSFixMe) => tag.name),
      count: this.taggableCount,
    });
  }

  private async bulkTagLeads(addedTags: $TSFixMe[]) {
    let appId = this.appService.app.id;
    let selectedIds = this.selectedUserIds;
    let hasSelection = selectedIds.length > 0;
    for (let tag of addedTags) {
      await post('/ember/users/bulk_tag.json', {
        app_id: appId,
        tag_name: tag.name,
        included_ids: hasSelection ? selectedIds : [],
        excluded_ids: [],
        predicates: hasSelection ? [] : this.currentLeadPredicates,
        count: this.taggableCount,
      });
    }
  }

  @action
  setExportAllAttributes(value: string) {
    this.exportAllAttributes = value === 'all';
  }

  @action
  async exportCsv() {
    let outcome = this.selectedOutcome;
    if (!outcome) {
      return;
    }
    try {
      await post('/ember/users/bulk_export.json', {
        app_id: this.appService.app.id,
        predicates: [
          {
            attribute: 'fin_qualification_status',
            type: 'string',
            comparison: 'eq',
            value: this.selectedOutcomeQualificationStatus,
          },
        ],
        included_ids: this.selectedUserIds,
        excluded_ids: [],
        count: this.exportCount,
        all_attributes: this.exportAllAttributes,
      });
      let exportedCount = this.exportCount;
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.analyze.leads.export-modal.success', {
          count: exportedCount,
        }),
      );
      this.closeExportModal();
      this.selectedUserMap = {};
    } catch {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.analyze.leads.export-modal.error'),
      );
    }
  }

  private buildConversationSearchUrl(user: UserInstance, outcome: RoutingOutcome): string {
    let predicates = {
      predicates: [
        {
          identifier: 'user',
          attribute: 'user_ids',
          comparison: 'eq',
          type: 'id',
          value: user.id.toString(),
        },
        {
          identifier: 'conversation-attribute',
          type: 'and',
          predicates: [
            {
              attribute: 'conversation.fin_sdr_participated',
              comparison: 'true',
              type: 'boolean',
              value: 'true',
            },
            {
              attribute: 'conversation.fin_sdr_routing_outcome',
              comparison: 'eq',
              type: 'string',
              value: outcome.identifier,
            },
          ],
        },
      ],
    };
    let encoded = B64FilterParamEncoder.b64Encode(JSON.stringify(predicates));
    let appId = this.appService.app.id;
    return `/a/inbox/${appId}/inbox/search?filters=${encoded}`;
  }

  get qualificationOverviewCharts() {
    let outcome = this.selectedOutcome;
    if (!outcome) {
      return [];
    }
    let outcomeFilter;
    if (outcome.identifier === 'disengaged') {
      outcomeFilter = {
        type: 'and',
        filters: [
          {
            type: 'category',
            data: {
              property: 'fin_sdr.participated',
              attribute: 'conversation.fin_sdr.participated',
              values: [true],
            },
          },
          {
            type: 'not_exists',
            data: {
              property: 'fin_sdr.routing_outcome.identifier',
              attribute: 'conversation.fin_sdr.routing_outcome_title',
            },
          },
        ],
      };
    } else {
      outcomeFilter = {
        type: 'and',
        filters: [
          {
            type: 'category',
            data: {
              attribute: 'conversation.fin_sdr.routing_outcome_title',
              property: 'fin_sdr.routing_outcome.title',
              values: [outcome.title],
            },
          },
        ],
      };
    }

    return [
      {
        ...DEFAULT_CHART_DEFINITION,
        id: `sdr_qualification_prospects_${outcome.identifier}`,
        title:
          outcome.identifier === 'disengaged'
            ? this.intl.t('ai-agent.analyze.leads.disengaged-prospects-count')
            : this.intl.t('ai-agent.analyze.leads.prospects-count', {
                outcome: outcome.title,
              }),
        visualization_type: VISUALIZATION_TYPES.COUNTER,
        visualization_options: { show_time_comparison: true },
        width_type: WIDTH_TYPES.THREE_COLUMN,
        height_type: HEIGHT_TYPES.X_SMALL,
        chart_series: [
          {
            metric_id: 'conversation.fin_sdr.participated.count',
            show_data_labels: true,
            filters: outcomeFilter,
            label:
              outcome.identifier === 'disengaged'
                ? this.intl.t('ai-agent.analyze.leads.disengaged-prospects-count')
                : this.intl.t('ai-agent.analyze.leads.prospects-count', {
                    outcome: outcome.title,
                  }),
          },
        ],
      },
      {
        ...DEFAULT_CHART_DEFINITION,
        id: `sdr_qualification_rate_${outcome.identifier}`,
        title:
          outcome.identifier === 'disengaged'
            ? this.intl.t('ai-agent.analyze.leads.disengaged-outcome-rate')
            : this.intl.t('ai-agent.analyze.leads.outcome-rate', {
                outcome: outcome.title,
              }),
        visualization_type: VISUALIZATION_TYPES.BESPOKE,
        component_name: 'sdr-leads/qualification-rates',
        width_type: WIDTH_TYPES.THREE_COLUMN,
        height_type: HEIGHT_TYPES.X_SMALL,
        chart_series: QualificationRatesConfig.getChartSeries(this.intl, outcome),
      },
    ];
  }

  get serializedReport() {
    return {
      id: 'fin-sdr-qualification',
      title: null,
      description: null,
      charts: [],
      filters: {},
      date_range: {
        selection: 'past_4_weeks',
      },
    };
  }

  get filterableProperties() {
    return ['time'];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAutomation::Analyze::Leads': typeof Leads;
    'ai-automation/analyze/leads': typeof Leads;
  }
}
